import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { db } from "@/lib/firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { Plus, Edit, Trash2, MapPin } from "lucide-react";

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  duration: string;
  totalHours: number;
  sessionHours: number;
  schedule: string;
  category: string;
  featured: boolean;
  branches: string[]; // Array of branch IDs
}

interface Branch {
  id: string;
  name: string;
}

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [message, setMessage] = useState<string>("");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: 0,
    duration: "",
    totalHours: 0,
    sessionHours: 4,
    schedule: "",
    category: "beginner",
    featured: false,
    branches: [] as string[]
  });

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Listen to courses
    const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Course[];
      setCourses(coursesData);
      setLoading(false);
    });
    unsubscribers.push(coursesUnsubscribe);

    // Listen to branches
    const branchesUnsubscribe = onSnapshot(collection(db, "branches"), (snapshot) => {
      const branchesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Branch[];
      setBranches(branchesData);
    });
    unsubscribers.push(branchesUnsubscribe);

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: 0,
      duration: "",
      totalHours: 0,
      sessionHours: 4,
      schedule: "",
      category: "beginner",
      featured: false,
      branches: []
    });
    setEditingCourse(null);
  };

  const handleOpenDialog = (course?: Course) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        title: course.title,
        description: course.description,
        price: course.price,
        duration: course.duration,
        totalHours: course.totalHours,
        sessionHours: course.sessionHours,
        schedule: course.schedule,
        category: course.category,
        featured: course.featured,
        branches: course.branches || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleBranchToggle = (branchId: string) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.includes(branchId)
        ? prev.branches.filter(id => id !== branchId)
        : [...prev.branches, branchId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || formData.price <= 0 || formData.branches.length === 0) {
      setMessage("Please fill in all required fields and select at least one branch.");
      return;
    }

    try {
      if (editingCourse) {
        // Update existing course
        await updateDoc(doc(db, "courses", editingCourse.id), {
          ...formData,
          updatedAt: new Date()
        });
        setMessage("Course updated successfully!");
      } else {
        // Add new course
        await addDoc(collection(db, "courses"), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setMessage("Course added successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving course:", error);
      setMessage("Error saving course. Please try again.");
    }
  };

  const handleDelete = async (courseId: string) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        await deleteDoc(doc(db, "courses", courseId));
        setMessage("Course deleted successfully!");
      } catch (error) {
        console.error("Error deleting course:", error);
        setMessage("Error deleting course. Please try again.");
      }
    }
  };

  const getBranchNames = (branchIds: string[]) => {
    return branchIds
      .map(id => branches.find(b => b.id === id)?.name || id)
      .join(", ");
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Course Management</h2>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Course
        </Button>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-300 text-blue-800 rounded">
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg">Loading courses...</div>
        </div>
      ) : (
        <div className="grid gap-4">
          {courses.map(course => (
            <Card key={course.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{course.title}</h3>
                      {course.featured && (
                        <Badge variant="secondary">Featured</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-3">{course.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Price:</span> ₱{course.price.toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Duration:</span> {course.duration}
                      </div>
                      <div>
                        <span className="font-medium">Total Hours:</span> {course.totalHours}h
                      </div>
                      <div>
                        <span className="font-medium">Category:</span> {course.category}
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Available at: {course.branches && course.branches.length > 0 
                          ? getBranchNames(course.branches)
                          : "All branches"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(course)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(course.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {courses.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No courses found. Add your first course to get started.
            </div>
          )}
        </div>
      )}

      {/* Course Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCourse ? "Edit Course" : "Add New Course"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Course Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Basic Driving Course"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-2 border rounded"
                >
                  <option value="beginner">Beginner</option>
                  <option value="advanced">Advanced</option>
                  <option value="specialized">Specialized</option>
                  <option value="refresher">Refresher</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Course description..."
                rows={3}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Price (₱) *</label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                  placeholder="5000"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Duration</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g., 15 Hours"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Total Hours</label>
                <Input
                  type="number"
                  value={formData.totalHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, totalHours: parseInt(e.target.value) || 0 }))}
                  placeholder="15"
                  min="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Session Hours</label>
                <Input
                  type="number"
                  value={formData.sessionHours}
                  onChange={(e) => setFormData(prev => ({ ...prev, sessionHours: parseInt(e.target.value) || 4 }))}
                  placeholder="4"
                  min="1"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Schedule</label>
              <Input
                value={formData.schedule}
                onChange={(e) => setFormData(prev => ({ ...prev, schedule: e.target.value }))}
                placeholder="e.g., Flexible Schedule"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Available Branches *</label>
              <div className="border rounded p-4 space-y-2 max-h-32 overflow-y-auto">
                {branches.map(branch => (
                  <div key={branch.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={branch.id}
                      checked={formData.branches.includes(branch.id)}
                      onChange={() => handleBranchToggle(branch.id)}
                      className="rounded"
                    />
                    <label htmlFor={branch.id} className="text-sm cursor-pointer">
                      {branch.name}
                    </label>
                  </div>
                ))}
              </div>
              {formData.branches.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.branches.length} branch(es) selected
                </p>
              )}
              {formData.branches.length === 0 && (
                <p className="text-sm text-red-500 mt-1">Please select at least one branch</p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="featured" className="text-sm cursor-pointer">
                Featured Course
              </label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingCourse ? "Update Course" : "Add Course"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Courses;
